import type { featuredArticles } from '@/mocks/home';

interface FeaturedSectionProps {
  articles: typeof featuredArticles;
}

export default function FeaturedSection({ articles }: FeaturedSectionProps) {
  return (
    <section className="bg-background-50 py-14 md:py-20">
      <div className="max-w-[1400px] mx-auto px-4 md:px-6">
        <div className="flex flex-col lg:flex-row gap-8 lg:gap-14">
          <div className="lg:w-1/2">
            <article className="relative rounded-2xl overflow-hidden bg-background-100 group cursor-pointer h-full">
              <Link to={`/article/${articles[0].slug}`} className="block h-full">
                <div className="relative aspect-[4/3] md:aspect-auto md:h-full overflow-hidden">
                  <img
                    src={articles[0].image}
                    alt={articles[0].title}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    loading="lazy"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent"></div>
                  <div className="absolute top-4 left-4 flex gap-2">
                    {articles[0].tags.map((tag) => (
                      <span key={tag} className="px-2.5 py-1 rounded-full bg-background-50/15 backdrop-blur-sm text-background-50 text-xs">
                        {tag}
                      </span>
                    ))}
                  </div>
                  <div className="absolute bottom-0 left-0 right-0 p-5 md:p-7">
                    <span className="text-primary-400 text-xs font-medium">{articles[0].category}</span>
                    <h3 className="mt-2 text-white text-xl md:text-2xl font-bold leading-snug">
                      {articles[0].title}
                    </h3>
                    <p className="mt-2 text-white/60 text-sm line-clamp-2">{articles[0].excerpt}</p>
                    <p className="mt-3 text-white/40 text-xs">{articles[0].date}</p>
                  </div>
                </div>
              </Link>
            </article>
          </div>

          <div className="lg:w-1/2 flex flex-col gap-4">
            {articles.slice(1).map((article) => (
              <Link
                key={article.id}
                to={`/article/${article.slug}`}
                className="flex gap-4 p-4 rounded-2xl bg-background-100 hover:bg-background-200/70 transition-colors duration-200 group cursor-pointer"
              >
                <div className="w-32 md:w-40 flex-shrink-0 rounded-xl overflow-hidden aspect-[4/3]">
                  <img
                    src={article.image}
                    alt={article.title}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    loading="lazy"
                  />
                </div>
                <div className="flex-1 min-w-0 flex flex-col justify-center">
                  <span className="text-primary-500 text-xs font-medium">{article.category}</span>
                  <h3 className="mt-1 text-foreground-900 text-sm md:text-base font-bold leading-snug line-clamp-2 group-hover:text-primary-600 transition-colors">
                    {article.title}
                  </h3>
                  <p className="mt-1 text-foreground-500 text-xs line-clamp-1">{article.excerpt}</p>
                  <div className="flex items-center gap-3 mt-2">
                    <span className="text-foreground-400 text-xs">{article.date}</span>
                    <div className="flex gap-1.5">
                      {article.tags.map((tag) => (
                        <span key={tag} className="px-2 py-0.5 rounded-full bg-secondary-100 text-secondary-700 text-[10px] font-medium">
                          {tag}
                        </span>
                      ))}
                    </div>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}